/**
 * Identity providers that can be linked to an already existing Cognito account.
 * Google is the only one today, but `/get-current-user` reports it as a list so
 * further providers can be added without another shape change.
 *
 * The backend spells these the way Cognito does (`"Google"`), so compare them
 * case-insensitively rather than against the literal.
 */
export type LinkedProvider = 'Google';

/**
 * `GET /link/google/start` answers with the Google consent URL the browser has
 * to be sent to. The redirect is deliberately performed by us and not by the
 * endpoint itself, because the call needs the Authorization header.
 */
export interface GoogleLinkStartResponse {
  authUrl: string;
}

/**
 * `POST /link/google/unlink` answers with the providers that are left, and with
 * `staleToken` when the call itself was made with a token issued before the
 * unlink. Such a token still carries the old link, and any request made with it
 * puts the link straight back on the profile - so the caller has to force a
 * token refresh and wait for it before reading the link state again.
 */
export interface GoogleUnlinkResponse {
  linkedProviders: LinkedProvider[];
  staleToken?: boolean;
}

/**
 * Failure codes the backend puts on the `reason` query param when it sends the
 * user back after a link attempt that did not succeed. Codes outside this list
 * are shown with the generic message.
 */
export type GoogleLinkErrorReason =
  | 'another_google_linked'
  | 'email_belongs_to_another_account'
  | 'google_email_linked_elsewhere'
  | 'already_linked_elsewhere'
  | 'google_email_has_own_contract'
  | 'link_failed'
  | 'access_denied'
  | 'email_unverified'
  | 'state_expired'
  | 'invalid_state'
  | 'missing_code'
  | 'exchange_failed'
  | 'not_configured';

/**
 * The message leaf under `account-preview.connected-accounts.messages.errors`
 * that each code is shown with.
 *
 * Several codes share one message on purpose. The screen only has to say what
 * the user can do next, and the three "this Google account belongs to somebody
 * else" codes all leave the same answer: nothing, here. The codes stay apart so
 * the console line still names the check that refused, which is what makes a
 * support ticket answerable.
 */
export const GOOGLE_LINK_ERROR_MESSAGE_KEYS: Readonly<Record<GoogleLinkErrorReason, string>> = {
  // The user has a different Google account connected and can disconnect it.
  another_google_linked: 'another-google-linked',

  // The Google account, or its address, belongs to another portal account.
  email_belongs_to_another_account: 'linked-elsewhere',
  google_email_linked_elsewhere: 'linked-elsewhere',
  already_linked_elsewhere: 'linked-elsewhere',

  // The address on the Google account has a contract of its own, and it is not
  // the address this portal account signs in with. No other portal account
  // holds it yet - it only qualifies for one, and a Google sign-in on it builds
  // that account. Linking it here would bind the address to this account
  // instead: the sign-up trigger would never run again, and the account the
  // second contract needs could never be built. So this code keeps a message of
  // its own, and that message offers no retry - it sends the user to sign out
  // and start a Google sign-in.
  google_email_has_own_contract: 'has-own-contract',

  // Nothing was proved and nothing was linked, so another attempt can work.
  link_failed: 'link-failed',
  invalid_state: 'link-failed',
  missing_code: 'link-failed',
  exchange_failed: 'link-failed',
  state_expired: 'expired',

  access_denied: 'access-denied',
  email_unverified: 'email-unverified',
  not_configured: 'not-configured'
};

export const GOOGLE_LINK_ERROR_REASONS =
  Object.keys(GOOGLE_LINK_ERROR_MESSAGE_KEYS) as readonly GoogleLinkErrorReason[];

/** Outcome of the Google round trip, parsed off the callback URL. */
export interface GoogleLinkCallbackResult {
  /**
   * `linked` is the fallback the contract-email callback uses while
   * `COGNITO_GOOGLE_LOGIN_URL` is not configured: the account exists and Google
   * is attached, but the customer still has to sign in. It lands on the account
   * page, which the customer cannot reach yet, so the login screen answers it.
   */
  status: 'connected' | 'linked' | 'error';
  /** Only set for `error`, and only when the backend sent a code we know. */
  reason?: GoogleLinkErrorReason;
  /**
   * The `reason` param exactly as it arrived, known code or not. Kept for the
   * console line, never for the copy.
   */
  rawReason?: string;
}
